import { useEffect } from "react";
import { useAppSelector } from "@/redux/hooks";

const DEFAULT_ACCENT = "#0487C2";

export default function TenantThemeProvider() {
  const tenant = useAppSelector((state) => state.tenant.loginTenant);

  useEffect(() => {
    const root = document.documentElement;
    const accent = tenant?.AccentColor || DEFAULT_ACCENT;
    const darker = darkenColor(accent, 0.15);
    const darkest = darkenColor(accent, 0.25);
    const lighter = lightenColor(accent, 0.12);
    const rgb = hexToRgb(accent);

    root.style.setProperty("--tenant-accent-color", accent);
    root.style.setProperty("--tenant-accent-color-darker", darker);
    root.style.setProperty("--tenant-accent-color-darkest", darkest);
    root.style.setProperty("--tenant-accent-color-lighter", lighter);
    if (rgb) {
      root.style.setProperty("--tenant-accent-rgb", rgb);
    }

    const title = tenant?.Name ? `Checkout · ${tenant.Name}` : "Checkout";
    document.title = title;

    if (tenant?.LogoUrl) {
      let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = tenant.LogoUrl;
    }

    return () => {
      root.style.removeProperty("--tenant-accent-color");
      root.style.removeProperty("--tenant-accent-color-darker");
      root.style.removeProperty("--tenant-accent-color-darkest");
      root.style.removeProperty("--tenant-accent-color-lighter");
      root.style.removeProperty("--tenant-accent-rgb");
    };
  }, [tenant?.AccentColor, tenant?.Name, tenant?.LogoUrl]);

  return null;
}

function hexToRgb(color: string): string | null {
  const hex = color.replace("#", "");
  if (hex.length !== 6) return null;
  const num = parseInt(hex, 16);
  if (Number.isNaN(num)) return null;
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  return `${r}, ${g}, ${b}`;
}

function darkenColor(color: string, amount: number): string {
  const hex = color.replace("#", "");
  const num = parseInt(hex, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.max(0, Math.floor(r * (1 - amount)));
  g = Math.max(0, Math.floor(g * (1 - amount)));
  b = Math.max(0, Math.floor(b * (1 - amount)));

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function lightenColor(color: string, amount: number): string {
  const hex = color.replace("#", "");
  const num = parseInt(hex, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.min(255, Math.floor(r + (255 - r) * amount));
  g = Math.min(255, Math.floor(g + (255 - g) * amount));
  b = Math.min(255, Math.floor(b + (255 - b) * amount));

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
